import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useResults } from '../hooks/useResults';
import { EvaluationScorecard } from '../components/EvaluationScorecard';
import { EvaluationStatus } from '../components/EvaluationStatus';
import { ResultError } from '../components/ResultError';
import { AdminPageHeader } from '../../admin/components/AdminPageHeader';
import { useTranslation } from '../../../i18n';

import api, { attempts as attemptsApi } from '../../../services/api';
import { showError } from '../../../utils/alerts';

export const ResultsPage: React.FC = () => {
  const { id: attemptId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, loading, error, isScoring, isInProgress, refresh } = useResults(attemptId);
  const [isSubmittingDirect, setIsSubmittingDirect] = React.useState(false);
  const [isRetrying, setIsRetrying] = React.useState(false);

  const handleEndAndSubmit = async () => {
    if (!attemptId || isSubmittingDirect) return;
    setIsSubmittingDirect(true);
    try {
      await attemptsApi.submit(attemptId, 'LEARNER_CONFIRMED');
      await refresh();
    } catch (err: any) {
      await showError('Submission Failed', err.message || 'Failed to submit attempt.');
    } finally {
      setIsSubmittingDirect(false);
    }
  };

  const effectiveLanguage =
    (data as any)?.selectedLanguage ||
    data?.language ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('trainova_preferred_language') : null) ||
    'en';

  const handleDirectRetry = async () => {
    if (!data?.simulationId || isRetrying) return;
    const simId = data.simulationId;
    setIsRetrying(true);
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('trainova_preferred_language', effectiveLanguage);
      }
      const res = await api.post<any>(`/api/v1/simulations/${simId}/attempts`, {
        language: effectiveLanguage,
      });
      const payload = res?.data ?? res;
      const newAttemptId = payload?.attempt?.id || payload?.attemptId || payload?.id;
      if (newAttemptId) {
        navigate(`/simulations/${simId}/run?attemptId=${newAttemptId}&lang=${effectiveLanguage}&retry=true`);
        return;
      }
      const resumeId = (res as any)?.error?.attemptId || (res as any)?.activeAttempt?.id || payload?.error?.attemptId;
      if (resumeId) {
        navigate(`/simulations/${simId}/run?attemptId=${resumeId}&lang=${effectiveLanguage}&retry=true`);
        return;
      }
      navigate(`/simulations/${simId}/briefing?lang=${effectiveLanguage}&retry=true`);
    } catch (err: any) {
      const errPayload = err?.response?.data || err;
      const errCode = errPayload?.error?.code || errPayload?.code;
      if (errCode === 'ACTIVE_ATTEMPT_EXISTS' && (errPayload?.error?.attemptId || errPayload?.activeAttempt?.id)) {
        const resumeId = errPayload?.error?.attemptId || errPayload?.activeAttempt?.id;
        navigate(`/simulations/${simId}/run?attemptId=${resumeId}&lang=${effectiveLanguage}&retry=true`);
        return;
      }
      navigate(`/simulations/${simId}/briefing?lang=${effectiveLanguage}&retry=true`);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="admin-page" style={{ display: 'grid', gap: '20px' }}>
      {/* Top Header with unified dark hero banner */}
      <AdminPageHeader
        breadcrumbs={[
          { label: t('common.home', undefined, 'Home'), path: '/dashboard' },
          { label: t('scorecard.my_attempts', undefined, 'My Attempts'), path: '/attempts' },
          { label: `${data?.simulationTitle || 'Simulation'} · ${t('scorecard.attempt_counter', { current: data?.attemptNumber || 1, max: data?.maxAttempts || 3 }, `Attempt #${data?.attemptNumber || 1}`)} ${t('scorecard.scorecard_title_suffix', undefined, 'Scorecard')}` },
        ]}
        icon="📊"
        title={data?.simulationTitle ? `${data.simulationTitle} — ${t('scorecard.scorecard_title_suffix', undefined, 'Scorecard')}` : t('scorecard.title', undefined, 'Simulation Evaluation Scorecard')}
        badge={
          data?.reportRestricted
            ? {
                label: t('scorecard.badge_completed', undefined, 'Completed'),
                bg: 'rgba(16, 185, 129, 0.25)',
                color: '#6ee7b7',
                border: '1px solid rgba(110, 231, 183, 0.4)',
              }
            : isScoring || (!data?.criteria?.length && data?.overallScore === undefined)
              ? {
                  label: t('scorecard.badge_ai_eval', undefined, 'AI Evaluation'),
                  bg: 'rgba(59, 130, 246, 0.2)',
                  color: '#93c5fd',
                  border: '1px solid rgba(147, 197, 253, 0.4)',
                }
              : data
                ? {
                    label: data.isPassed ? t('scorecard.badge_passed', undefined, 'Passed') : t('scorecard.badge_needs_review', undefined, 'Needs Review'),
                    bg: data.isPassed ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                    color: data.isPassed ? '#6ee7b7' : '#fca5a5',
                    border: `1px solid ${data.isPassed ? 'rgba(110, 231, 183, 0.4)' : 'rgba(252, 165, 165, 0.4)'}`,
                  }
                : t('scorecard.badge_ai_eval', undefined, 'AI Evaluation')
        }
        description={
          data?.reportRestricted
            ? 'Assessment has been submitted. Review and evaluation are managed by your organization administrators.'
            : 'Comprehensive AI competency rubric scores, speech & delivery metrics, video body language observations, and personalized coaching.'
        }
        actions={
          <button
            type="button"
            onClick={() => navigate('/attempts')}
            className="admin-btn admin-btn--secondary"
            style={{
              height: '36px',
              padding: '0 14px',
              background: 'rgba(255, 255, 255, 0.12)',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              borderRadius: '8px',
              fontSize: '12.5px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {t('scorecard.all_attempts_btn', undefined, '‹ All Attempts')}
          </button>
        }
      />

      {loading && !data ? (
        <div
          style={{
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: '16px',
            padding: '60px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            minHeight: '380px',
            boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)',
          }}
        >
          <div
            className="dashboard-spinner"
            style={{ width: '40px', height: '40px', borderWidth: '3px', borderColor: '#2563eb', borderTopColor: 'transparent' }}
          />
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 700, color: 'var(--ink)' }}>
              {t('scorecard.analyzing_attempt', undefined, 'Analyzing Attempt Performance...')}
            </h4>
            <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--muted)' }}>
              {t('scorecard.evaluating_subtext', undefined, 'Evaluating speech delivery, conversational rubrics, and generating behavioral coaching feedback.')}
            </p>
          </div>
        </div>
      ) : error && !data ? (
        <ResultError message={error} onRetry={refresh} />
      ) : isScoring ? (
        <EvaluationStatus
          status={data?.status || 'SCORING'}
          message={data?.message}
          onRefresh={refresh}
        />
      ) : isInProgress ? (
        <div style={{ maxWidth: '560px', margin: '40px auto', padding: '32px', background: 'var(--white)', borderRadius: '14px', border: '1px solid var(--line)', textAlign: 'center', display: 'grid', gap: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: '20px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
            {t('scorecard.in_progress_title', undefined, 'Attempt Is In Progress')}
          </h2>
          <p style={{ fontSize: '13.5px', color: 'var(--muted)', margin: 0, lineHeight: 1.5 }}>
            {t('scorecard.in_progress_desc', undefined, 'This simulation attempt has not yet been submitted for evaluation. You can resume and finish the dialogue session, or submit it now to generate your evaluation report.')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', paddingTop: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== 'undefined') sessionStorage.setItem('trainova_preferred_language', effectiveLanguage);
                navigate(`/simulations/${data?.simulationId}/run?attemptId=${attemptId}&lang=${effectiveLanguage}`);
              }}
              className="admin-btn admin-btn--primary"
            >
              {t('scorecard.resume_simulation', undefined, 'Resume Simulation')}
            </button>
            <button
              type="button"
              onClick={handleEndAndSubmit}
              disabled={isSubmittingDirect}
              className="admin-btn admin-btn--secondary"
              style={{
                borderColor: 'rgba(239, 68, 68, 0.4)',
                color: '#ef4444',
                fontWeight: 600,
              }}
            >
              {isSubmittingDirect ? t('scorecard.submitting_evaluating', undefined, 'Submitting & Evaluating...') : t('scorecard.end_and_submit', undefined, 'End & Submit for Evaluation')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/attempts')}
              className="admin-btn admin-btn--secondary"
            >
              {t('scorecard.my_attempts', undefined, 'My Attempts')}
            </button>
          </div>
        </div>
      ) : data?.reportRestricted ? (
        <div style={{ maxWidth: '640px', width: '100%', margin: '30px auto', padding: '36px clamp(16px, 4vw, 32px)', background: 'var(--white)', borderRadius: '16px', border: '1px solid var(--line)', textAlign: 'center', display: 'grid', gap: '18px', boxShadow: '0 4px 20px rgba(7, 29, 69, 0.05)', boxSizing: 'border-box' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'var(--ok-soft)', color: 'var(--ok)', display: 'grid', placeItems: 'center', margin: '0 auto', fontSize: '26px', fontWeight: 700 }}>
            ✓
          </div>
          <div>
            <h2 style={{ fontFamily: 'var(--display)', fontSize: '22px', fontWeight: 700, color: 'var(--navy)', margin: '0 0 8px' }}>
              {t('scorecard.assessment_completed_title', undefined, 'Assessment Completed')}
            </h2>
            <p style={{ fontSize: '14.5px', color: 'var(--muted)', margin: 0, lineHeight: 1.6 }}>
              {data.message || 'Your simulation attempt has been successfully recorded and submitted. Detailed performance scorecards and feedback reports are restricted for learners by your organization administrator.'}
            </p>
          </div>

          <div style={{ background: 'var(--surface)', padding: '16px 20px', borderRadius: '12px', border: '1px solid var(--line)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', textAlign: 'left' }}>
            <div>
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('common.simulation', undefined, 'Simulation')}</span>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink)', marginTop: '2px' }}>{data.simulationTitle || 'Simulation'}</div>
            </div>
            <div>
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('common.attempt', undefined, 'Attempt')}</span>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink)', marginTop: '2px' }}>#{data.attemptNumber || 1} of {data.maxAttempts || 3}</div>
            </div>
            <div>
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('common.status_label', undefined, 'Status')}</span>
              <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ok)', marginTop: '2px' }}>{t('scorecard.badge_completed', undefined, 'Completed')}</div>
            </div>
          </div>

          {(data.videoUrl || data.videoRecording?.publicUrl) && (
            <div style={{ marginTop: '12px', textAlign: 'left' }}>
              <h4 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('scorecard.session_recording_title', undefined, 'Your Session Recording')}
              </h4>
              <video
                src={data.videoUrl || data.videoRecording?.publicUrl}
                controls
                playsInline
                style={{ width: '100%', maxHeight: '280px', borderRadius: '10px', background: '#000' }}
              />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', paddingTop: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="admin-btn admin-btn--primary"
            >
              {t('scorecard.dashboard_btn', undefined, 'Dashboard')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/attempts')}
              className="admin-btn admin-btn--secondary"
            >
              {t('scorecard.my_attempts', undefined, 'My Attempts')}
            </button>
            {data.simulationId && (
              <button
                type="button"
                onClick={handleDirectRetry}
                disabled={isRetrying}
                className="admin-btn admin-btn--secondary"
              >
                {isRetrying ? 'Starting…' : t('scorecard.retake_simulation', undefined, 'Retake Simulation')}
              </button>
            )}
          </div>
        </div>
      ) : data ? (
        <EvaluationScorecard
          data={data}
          onSelectAttempt={(newAttemptId) => navigate(`/attempts/${newAttemptId}/results`)}
          onReevaluate={() => refresh(true)}
          onRetry={handleDirectRetry}
        />
      ) : null}
    </div>
  );
};
